<template>
  <div>
    <div
      v-if="showSuccessMessage"
      v-html="successMessage"
    />
    <div v-else>
      <div class="newsletter-form__text">
        <slot name="pre-form" />
      </div>

      <form
        ref="form"
        :action="formActionUrl"
        method="post"
        @submit.prevent="validateAndSend"
      >
        <div class="newsletter-form__input-wrapper">
          <label
            for="newsletter-sign-up"
            class="is-sr-only"
            v-text="emailPlaceholder"
          />
          <input
            id="newsletter-sign-up"
            v-model="emailField"
            type="email"
            :class="{ 'input-error': hasErrorValidEmail || hasErrorEmail }"
            class="newsletter-form__input"
            autocomplete="off"
            :placeholder="emailPlaceholder"
            :aria-describedby="emailDescribedby"
            @blur="checkEmailValid(), isEmailEmpty()"
          />
          <button
            type="submit"
            :class="{ disabled: !isSubmitEnabled }"
            class="btn__submit"
            @click="checkEmailValid(), isConsentEmpty(), isEmailEmpty()"
          >
            {{ submitBtnText }}
          </button>
        </div>

        <label
          for="newsletter-form__consent"
          class="newsletter-form__consent checkbox-custom-label"
        >
          <input
            id="newsletter-form__consent"
            v-model="isConsentChecked"
            :aria-describedby="consentCheckId"
            type="checkbox"
          />
          <span class="newsletter-form__privacy">
            <slot name="consent-terms" />
          </span>
        </label>

        <div
          v-if="hasErrorValidEmail"
          :id="invalidFieldId"
          class="newsletter-form__error"
        >
          {{ validationMsg.emailFormat }}
        </div>
        <div
          v-if="hasErrorEmail"
          :id="emptyFieldId"
          class="newsletter-form__error"
        >
          {{ validationMsg.emailEmpty }}
        </div>
        <div
          v-if="hasErrorConsent"
          :id="consentCheckId"
          class="newsletter-form__error"
          v-text="validationMsg.terms"
        />
        <div
          v-if="errorMessage"
          :id="errorMessageId"
          class="newsletter-form__error"
          v-text="errorMessage"
        />
      </form>
    </div>
  </div>
</template>

<script>
import { recaptcha } from '@Foundation/mixins/recaptcha';

export default {
  name: 'NewsletterSignUp',

  mixins: [recaptcha],

  props: {
    datasourceId: { type: String, default: '' },
    formActionUrl: { type: String, default: '' },
    emailPlaceholder: { type: String, default: '' },
    recaptcha: { default: null, type: Object },
    submitBtnText: { type: String, default: '' },
    successMessage: {
      default: '',
      type: String,
    },
    validationMsg: {
      type: Object,
      default: () => {
        return {
          emailFormat: 'Please use valid email format.',
          emailEmpty: 'Please enter your email address.',
          terms: 'Please accept the terms of use.',
        };
      },
    },
  },
  data() {
    return {
      errorMessage: null,
      errorMessageId: 'newsletter-sign-up-error',
      isConsentChecked: false,
      isSubmitEnabled: false,
      emailField: '',
      hasErrorConsent: false,
      hasErrorValidEmail: false,
      hasErrorEmail: false,
      invalidFieldId: `${Math.random(1000).toFixed(0)}__${Date.now()}__invalid`,
      consentCheckId: `${Math.random(1000).toFixed(0)}__${Date.now()}__consent`,
      emptyFieldId: `${Math.random(1000).toFixed(0)}__${Date.now()}__empty`,
      showSuccessMessage: false,
    };
  },
  computed: {
    canSubmit() {
      return !!this.emailField && !this.hasErrorValidEmail && !this.hasErrorConsent;
    },
    emailDescribedby() {
      if (!!this.emailField && this.hasErrorValidEmail) return this.invalidFieldId;
      if (!this.emailField && this.hasErrorEmail) return this.emptyFieldId;
      if (this.errorMessage) return this.errorMessageId;
      return false;
    },
  },
  watch: {
    canSubmit(value) {
      this.isSubmitEnabled = value;
    },
  },

  mounted() {
    if (this.recaptcha) {
      this.loadRecaptchaOnInput(this.$refs.form, this.recaptcha.siteKey);
    }
  },

  methods: {
    isConsentEmpty() {
      const isChecked = this.isConsentChecked;
      this.hasErrorConsent = !isChecked;
      return this.hasErrorConsent;
    },
    isEmailEmpty() {
      if (this.emailField === '') {
        this.hasErrorEmail = true;
      } else {
        this.hasErrorEmail = false;
      }
    },
    validateEmail(email) {
      const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      return re.test(String(email).toLowerCase());
    },
    checkEmailValid() {
      if (!this.validateEmail(this.emailField) && this.emailField !== '') {
        this.hasErrorValidEmail = true;
      } else {
        this.hasErrorValidEmail = false;
      }
    },
    async send() {
      const requestBody = {
        email: this.emailField,
        datasourceId: this.datasourceId,
      };

      if (this.recaptcha) {
        requestBody.recaptchaToken = await this.generateToken(this.recaptcha.siteKey);
      }

      await fetch(this.formActionUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json;charset=utf-8' },
        credentials: 'same-origin',
        body: JSON.stringify(requestBody),
      })
        .then((response) => response.json())
        .then((data) => {
          if (this.recaptcha && !data.RecaptchaSuccess && this.validationMsg.recaptcha) {
            throw new Error(this.validationMsg.recaptcha)
          }

          if (!data.Success) {
            throw new Error(data.Message)
          }

          this.showSuccessMessage = true;
        })
        .catch((error) => {
          this.errorMessage = error.message;
        });
    },
    validateAndSend() {
      this.errorMessage = null;
      this.isConsentEmpty();
      this.isEmailEmpty();

      if (!this.emailField || !this.isSubmitEnabled) {
        return;
      }

      this.send();
    },
  },
};
</script>
